import React, { memo } from 'react';

import { StyledDisplayPrice } from './DisplayPrice.styles';

import { DisplayPriceProps } from './DisplayPrice.types';

import { getCurrencySymbols } from '../../../';

//TODO Remove Duplicate "customClass" prop (duplicate of "customClassnames")
const DisplayPrice = ({
  price,
  currency,
  discount,
  skuKey,
  displayZeroAsFree,
  VATIncluded,
  customFreeMsg,
  alwaysBold,
  includeMicroData = true,
  decimalSeparator = '.',
  canShorten: canShortenProp,
  country,
  customClass = '',
  componentClassname = 'display-price',
  customClassnames = '',
  customStyle = '',
  dataQA,
}: DisplayPriceProps) => {
  const [currencySymbolLeft, currencySymbolRight] = getCurrencySymbols(
    currency,
    country,
  );

  const getInt = (amount: number) => {
    const roundedAmount = Math.round(Math.abs(amount));
    return `${(
      (roundedAmount - (roundedAmount % 100)) /
      100
    ).toLocaleString()}`;
  };

  const getCents = (amount: number) => {
    let canShorten = canShortenProp;
    if (
      currency === 'HKD' ||
      currency === 'NOK' ||
      currency === 'DKK' ||
      currency === 'SEK'
    ) {
      canShorten = true;
    }
    const roundedAmount = Math.round(Math.abs(amount));
    const cents = `0${roundedAmount}`.slice(-2);
    if (canShorten && cents === '00') {
      return '';
    }
    return decimalSeparator + cents;
  };

  // Microdata docs:
  //  https://schema.org/PriceSpecification
  //  https://search.google.com/structured-data/testing-tool
  const priceWithMicrodata = (
    amount: number,
    discountStartDate?: Date,
    discountEndDate?: Date,
  ) => {
    if (displayZeroAsFree && amount === 0) {
      return <span data-skukey={skuKey}>{customFreeMsg || 'Free'}</span>;
    }
    return (
      <span>
        {VATIncluded ? (
          <meta
            itemProp="valueAddedTaxIncluded"
            content={VATIncluded ? 'true' : 'false'}
          />
        ) : null}
        {discountStartDate ? (
          <meta
            itemProp="validFrom"
            content={discountStartDate.toISOString()}
          />
        ) : null}
        {discountEndDate ? (
          <meta
            itemProp="validThrough"
            content={discountEndDate.toISOString()}
          />
        ) : null}
        <meta itemProp="priceCurrency" content={currency} />
        <meta itemProp="price" content={(amount / 100).toFixed(2)} />
        <span className="display-price__currency-symbol display-price-base__currency-symbol--left">
          {currencySymbolLeft}
        </span>
        <span className={`${componentClassname}__amount`}>
          {getInt(amount)}
          {!!getCents(amount).length && (
            <span className={`${componentClassname}__amount__cents`}>
              {getCents(amount)}
            </span>
          )}
        </span>
        <span
          className={`${componentClassname}__currency-symbol ${componentClassname}-base__currency-symbol--right`}
        >
          {currencySymbolRight}
        </span>
      </span>
    );
  };

  const plainPrice = (amount: number) => {
    if (displayZeroAsFree && amount === 0) {
      return <span data-skukey={skuKey}>{customFreeMsg || 'Free'}</span>;
    }
    return (
      <span>
        <span
          className={`${componentClassname}__currency-symbol ${componentClassname}__currency-symbol--left`}
        >
          {currencySymbolLeft}
        </span>
        <span className={`${componentClassname}__amount`}>
          {getInt(amount)}
          {!!getCents(amount).length && (
            <span className={`${componentClassname}__amount__cents`}>
              {getCents(amount)}
            </span>
          )}
        </span>
        <span
          className={`${componentClassname}__currency-symbol ${componentClassname}__currency-symbol--right`}
        >
          {currencySymbolRight}
        </span>
      </span>
    );
  };

  const hasDiscountedPrice = !!(discount && typeof discount.price === 'number');

  return (
    <StyledDisplayPrice
      className={`${componentClassname} ${customClassnames} ${customClass}`}
      customStyle={customStyle}
      alwaysBold={alwaysBold}
      discounted={hasDiscountedPrice}
      data-qa={dataQA ? `display-price-${dataQA}` : 'display-price'}
      data-have-discount={discount && discount.price ? 'true' : undefined}
      data-is-free={displayZeroAsFree && price && price === 0}
    >
      <span
        className={`${componentClassname}__base-price`}
        data-skukey={!hasDiscountedPrice ? skuKey : undefined}
        data-qa={dataQA ? `display-price-base-${dataQA}` : 'display-price-base'}
      >
        {includeMicroData && !hasDiscountedPrice
          ? priceWithMicrodata(price)
          : plainPrice(price)}
      </span>
      {discount && hasDiscountedPrice && (
        <span
          className={`${componentClassname}__discounted-price`}
          data-qa={
            dataQA
              ? `display-price-discounted-${dataQA}`
              : 'display-price-discounted'
          }
          data-skukey={skuKey}
        >
          {includeMicroData
            ? priceWithMicrodata(
                discount.price,
                discount.startDate,
                discount.endDate,
              )
            : plainPrice(discount.price)}
        </span>
      )}
    </StyledDisplayPrice>
  );
};
DisplayPrice.displayName = 'DisplayPrice';

export default memo(DisplayPrice);
